import { API_BASE_URL } from '@/features/home/data/codeSamples'
import { Logo } from '@/features/shared/components/Logo'
import { NavLink } from '@/features/shared/components/NavLink'

const COLUMNS = [
  {
    heading: 'Platform',
    links: [
      { to: '/models', label: 'Supported Models' },
      { to: '/blogs', label: 'Blog', search: { page: 1 } },
      { to: '/changelog', label: 'Changelog' },
      { to: '/status', label: 'System Status' },
      { to: '/support', label: 'Support' },
    ],
  },
  {
    heading: 'Developers',
    links: [
      { to: '/docs/introduction', label: 'Documentation' },
      { to: '/docs/api-reference/overview', label: 'API Reference' },
      { to: '/sitemap.xml', label: 'Sitemap', plain: true },
    ],
  },
  {
    heading: 'Company',
    links: [
      { to: '/about', label: 'About' },
      { to: '/privacy', label: 'Privacy Policy' },
      { to: '/terms', label: 'Terms of Service' },
      { to: '/data-processing', label: 'Data Processing' },
    ],
  },
] as const

/** Mirrors NavLink's base styling for anchors that bypass the router. */
const PLAIN_LINK_CLASS =
  'inline-flex min-h-11 items-center rounded-full text-sm font-medium text-[var(--color-text-secondary)] transition-colors duration-300 ease-brisk hover:text-[var(--color-text-primary)] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary/50'
const SOCIAL_CLASS =
  'inline-flex min-h-11 items-center gap-1.5 rounded-full text-body-sm text-[var(--color-text-tertiary)] transition-colors duration-300 ease-brisk hover:text-[var(--color-text-primary)] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary/50'

export function Footer() {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="mt-auto border-t border-[var(--color-border)] bg-[var(--color-bg-secondary)]/50">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-2 gap-10 py-14 lg:grid-cols-12 lg:gap-8">
          {/* Brand Column */}
          <div className="col-span-2 lg:col-span-5">
            <Logo size="sm" />
            <p className="mt-5 max-w-sm text-body-sm leading-relaxed text-[var(--color-text-secondary)]">
              Every model behind one OpenAI-compatible endpoint. Built for
              developers who want the control plane shown as it really is.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-1.5 font-mono text-caption text-[var(--color-text-tertiary)]">
              <span className="rounded-full bg-[var(--color-bg-tertiary)] px-2 py-0.5 font-semibold text-[var(--color-text-secondary)]">
                base_url
              </span>
              <span className="text-[var(--color-text-primary)]">
                {API_BASE_URL}
              </span>
            </div>
          </div>

          {COLUMNS.map((column) => (
            <div key={column.heading} className="lg:col-span-2">
              <h3 className="mb-3 text-caption font-semibold uppercase tracking-[0.12em] text-[var(--color-text-tertiary)]">
                {column.heading}
              </h3>
              <ul className="flex flex-col">
                {column.links.map((link) => (
                  <li key={link.to}>
                    {'plain' in link ? (
                      <a href={link.to} className={PLAIN_LINK_CLASS}>
                        {link.label}
                      </a>
                    ) : (
                      <NavLink
                        to={link.to}
                        search={'search' in link ? link.search : undefined}
                      >
                        {link.label}
                      </NavLink>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col items-center justify-between gap-3 border-t border-[var(--color-border)] py-5 md:flex-row">
          <p className="font-mono text-caption tabular-nums text-[var(--color-text-tertiary)]">
            © {currentYear} ModelStack. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="https://t.me/louci_san"
              target="_blank"
              rel="noopener noreferrer"
              className={SOCIAL_CLASS}
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
              </svg>
              Telegram
            </a>
            <span aria-hidden className="h-4 w-px bg-[var(--color-border)]" />
            <a
              href="https://discord.gg/QEgfe3xD"
              target="_blank"
              rel="noopener noreferrer"
              className={SOCIAL_CLASS}
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z" />
              </svg>
              Discord
            </a>
          </div>
        </div>
      </div>

      {/* Oversized wordmark as a quiet sign-off; decorative only. */}
      <div aria-hidden className="pointer-events-none select-none overflow-hidden">
        <p className="text-sheen -mb-[0.22em] text-center text-[clamp(4rem,17vw,16rem)] font-semibold leading-none tracking-[-0.06em] opacity-40">
          ModelStack
        </p>
      </div>
    </footer>
  )
}
